import { useViewportWidth } from '@redbubble/next/hooks';
import { DeviceType } from '@redbubble/next/types';
import { Elements } from '@stripe/react-stripe-js';
import {
  loadStripe,
  AddressMode as StripeAddressMode,
  StripeElementLocale,
  StripeElementsOptions,
  StripeElementsOptionsMode,
} from '@stripe/stripe-js';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { EXTERNAL_PAYPAL_METHOD_TYPE } from './constants';
import { getStripePublishableKey, getSubunitAmount } from './utils';

// XXX (jj): the type for locales used in frontend is just a string,
// however, the stripe locale type is more structured. e.g., "en" | "es" | ...
// in liu of a more structured types and validations for locales in frontend,
// we are relying that the stripe SDK will raise an error (which we can handle)
// if we pass an unknown locale
const validateLocale = (rawLocale: string) => {
  return rawLocale as StripeElementLocale;
};

const getAppearance = (
  isDesktop: boolean
): StripeElementsOptions['appearance'] => {
  return {
    variables: {
      fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
      gridRowSpacing: isDesktop ? '32px' : '24px',
    },
    rules: {
      '.Label': {
        color: '#19124F',
        fontSize: '16px',
        lineHeight: '26px',
        fontWeight: '600',
      },
      '.Input': {
        boxShadow: 'none',
        border: '2px solid #D6DADF',
      },
      '.AccordionItem': {
        border: '1px solid #D6DADF',
      },
      '.RadioIcon': {
        width: '24px',
      },
      '.RadioIconOuter': {
        stroke: '#D6DADF',
      },
      '.RadioIconOuter--checked': {
        stroke: '#19124F',
        fill: '#19124F',
      },
      '.RadioIconInner': {
        fill: 'white',
        r: '16',
      },
    },
  };
};

const paymentOptions: StripeElementsOptionsMode = {
  mode: 'payment',
  captureMethod: 'manual',
};

export interface StripeElementsWrapperProps {
  currency: string;
  locale: string;
  /** `disabledAddressElement` allow the parent to decide which Stripe Address Element can be mounted
   * onto the DOM.
   *
   * This is useful when the 'shipping' and 'billing' Stripe Address Element needs to be presented
   * to the customer, but only ONE address element is visible to customer at any time. Rendering
   * the address element one at a time, it disable the Stripe SDK from display the Stripe's version of
   * 'Same billing & shipping address' checkbox.
   *
   * The reason why we wouldn't want to use Stripe's 'Same billing & shipping address' checkbox is because
   * Stripe does not give the checkbox control to the consumer. So, the consumer cannot set the initial state
   * or change the label text.
   */
  disabledAddressElement?: StripeAddressMode;
  amount?: number;
  onLoadError?: (reason?: string) => void;
  children?: React.ReactNode;
}

export type StripeElementsContextValue = {
  options?: StripeElementsOptions;
  disabledAddressElement?: StripeAddressMode;
};

export const StripeElementsContext = createContext<StripeElementsContextValue>(
  {}
);

export const StripeElementsContextProvider: React.FC<
  StripeElementsWrapperProps
> = ({
  currency,
  locale,
  amount,
  onLoadError,
  disabledAddressElement,
  children,
}) => {
  // The `statefulOptions` state is used to provide options to Context Consumers
  // (e.g., Stripe Address Element and Stripe Payment Element) while avoiding a race condition.
  //
  // If `stripeElementOptions` is passed to both `<Elements />` and Context consumers, a race
  // condition will occur when the `amount` transitions from a undefined or 0 to a number that's bigger than 0:
  // - The `stripeElementOptions` get updated and passed to both `<Elements />` and Context Consumers.
  // - If `<PaymentElement />` depends on `stripeElementOptions.mode === 'payment'`, it will attempt to render
  //   before `<Elements />` finishes processing the updated `stripeElementOptions`.
  // - Stripe SDK throws an error because `<PaymentElement />` requires the updated `stripeElementOptions`
  //   to be fully computed in `<Elements />`.
  //
  // The usage of `statefulOptions` and `stripeElementOptions` attempt to:
  // - Pass `stripeElementOptions` to `<Elements />` to ensure it computes the updated `stripeElementOptions` first.
  // - Pass `statefulOptions` to Context Consumers, ensuring they only receive the updated `statefulOptions`
  //   after `<Elements />` has finished processing `stripeElementOptions`.
  //
  // By passing the `statefulOptions` to the Context Consumers, and `stripeElementOptions` to `<Elements />`,
  // it means that by the time that `<PaymentElements />` is committed to the DOM, the `<Elements />` has
  // finished computing the new `stripeElementOptions` value.
  const [statefulOptions, setStatefulOptions] = useState<
    StripeElementsOptions | undefined
  >(undefined);

  const { isOrAbove } = useViewportWidth();
  const isDesktop = isOrAbove(DeviceType.Desktop);

  const stripeElementOptions: StripeElementsOptions = useMemo(() => {
    const defaultOptions = {
      currency: currency.toLowerCase(),
      locale: validateLocale(locale),
      appearance: getAppearance(isDesktop),
      externalPaymentMethodTypes: [EXTERNAL_PAYPAL_METHOD_TYPE],
    };

    if (!amount) {
      return defaultOptions;
    }

    return {
      ...defaultOptions,
      ...paymentOptions,
      amount: getSubunitAmount(amount),
    };
  }, [currency, amount, locale, isDesktop]);

  // NOTE (CY): useMemo() is used here so that we don't always reload
  // the Stripe SDK every time this component re-renders
  const stripePromise = useMemo(async () => {
    try {
      const key = getStripePublishableKey({
        currencyCode: currency.toUpperCase(),
      });

      const stripe = await loadStripe(key);

      if (!stripe) {
        throw new Error('Stripe SDK returned null');
      }

      return stripe;
    } catch (error) {
      onLoadError?.(`Error loading Stripe SDK: ${error}`);
    }

    return null;
  }, [currency, onLoadError]);

  useEffect(() => {
    setStatefulOptions(stripeElementOptions);
  }, [stripeElementOptions]);

  const value = useMemo(() => {
    return {
      options: statefulOptions,
      disabledAddressElement,
    };
  }, [statefulOptions, disabledAddressElement]);

  return (
    <StripeElementsContext.Provider value={value}>
      <Elements stripe={stripePromise} options={stripeElementOptions}>
        {children}
      </Elements>
    </StripeElementsContext.Provider>
  );
};

export function useStripeElementsContext() {
  const context = useContext<StripeElementsContextValue>(StripeElementsContext);

  if (context === undefined) {
    throw new Error(
      `useStripeElementsContext must be used within a StripeElementsContext Provider`
    );
  }

  return context;
}
